import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Footer from '../../components/Footer';

// Shared opt-out cookie — scoped to .openbooq.com so a single choice here applies
// across the marketing site AND the app (portal.openbooq.com). The in-<head> pixel
// gate on every page reads this same cookie (and the GPC signal) before loading any
// ad/conversion tag.
const OPT_OUT_COOKIE = 'obq_ad_optout';
const TEN_YEARS_SECONDS = 10 * 365 * 24 * 60 * 60;

function hasOptOutCookie() {
  if (typeof document === 'undefined') return false;
  return (' ' + (document.cookie || '')).indexOf(' ' + OPT_OUT_COOKIE + '=1') > -1;
}

function gpcEnabled() {
  if (typeof navigator === 'undefined') return false;
  return navigator.globalPrivacyControl === true || navigator.globalPrivacyControl === '1';
}

const PrivacyChoices = () => {
  const [optedOut, setOptedOut] = useState(false);
  const [gpc, setGpc] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setGpc(gpcEnabled());
    setOptedOut(hasOptOutCookie());
  }, []);

  const applyChoice = (optOut) => {
    if (optOut) {
      document.cookie =
        OPT_OUT_COOKIE + '=1; domain=.openbooq.com; path=/; max-age=' + TEN_YEARS_SECONDS + '; SameSite=Lax';
    } else {
      document.cookie = OPT_OUT_COOKIE + '=; domain=.openbooq.com; path=/; max-age=0; SameSite=Lax';
    }
    // Reload so the in-<head> pixel gate re-evaluates immediately with the new choice.
    window.location.reload();
  };

  const effectiveOptedOut = optedOut || gpc;

  return (
    <div className="min-h-screen bg-surface">
      <Helmet>
        <title>Your Privacy Choices | OpenbooQ</title>
        <meta
          name="description"
          content="Opt out of advertising and conversion tracking on OpenbooQ. We automatically honor your browser's Global Privacy Control signal — no cookie banner required."
        />
      </Helmet>
      <Header />

      <section className="pt-24 pb-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Shield" size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-jakarta font-bold text-text-primary">Your Privacy Choices</h1>
              <p className="text-text-secondary mt-1">Control how we use data for advertising</p>
            </div>
          </div>

          <p className="text-lg text-text-secondary mb-8">
            OpenbooQ uses advertising and conversion-measurement technologies (such as our conversion
            pixels and Google/Meta advertising tags) to understand which marketing brings homeowners to
            us. You can opt out of that ad and conversion tracking here. We do not sell your personal
            information, and this choice does not affect the roofing service you receive.
          </p>

          {gpc && (
            <div className="rounded-xl border border-green-500/30 bg-green-50 p-4 mb-6 flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
              <div className="text-sm text-text-primary">
                <span className="font-semibold">Global Privacy Control detected.</span> Your browser is
                sending a universal opt-out signal, so you&apos;re already opted out of ad and conversion
                tracking across OpenbooQ — no action needed.
              </div>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm mb-8">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-semibold text-text-primary">Ad &amp; conversion tracking</div>
                <div className="text-sm text-text-secondary mt-1">
                  {effectiveOptedOut
                    ? 'You are opted OUT — ad and conversion pixels are turned off.'
                    : 'You are opted IN — ad and conversion pixels are on.'}
                </div>
              </div>
              {gpc ? (
                <span className="text-sm font-semibold text-green-700">Opted out via GPC</span>
              ) : optedOut ? (
                <Button variant="outline" onClick={() => applyChoice(false)}>
                  Turn tracking back on
                </Button>
              ) : (
                <Button variant="default" onClick={() => applyChoice(true)}>
                  Opt out of ad tracking
                </Button>
              )}
            </div>
          </div>

          <div className="text-sm text-text-secondary space-y-3">
            <p>
              <span className="font-semibold text-text-primary">What this controls:</span> advertising and
              conversion tags — our CTV conversion pixel and the Google (Ads / Analytics) and Meta tags
              loaded through our tag manager. Opting out stops those from loading. Essential site
              functionality and the first-party analytics we use to run the site may still operate.
            </p>
            <p>
              <span className="font-semibold text-text-primary">Global Privacy Control:</span> if your
              browser or a privacy extension sends the GPC signal, we honor it automatically as an opt-out
              — you don&apos;t need to do anything here.
            </p>
            <p>
              Your choice is stored in a cookie on your device and applies across openbooq.com and our app.
              If you clear cookies or switch browser or device, set it again here.
            </p>
            <p>
              For full details on what we collect and how we use it, see our{' '}
              <Link to="/privacy-policy" className="text-primary font-semibold hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyChoices;
